import React, { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { ModelHealthTab } from '../components/ModelHealthTab';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/utils';
import { Loader2, CircleAlert, ClipboardCheck, ShieldAlert, Users } from 'lucide-react';

// ---- Types ----

interface OpsPortalConfig {
  apiKey: string;
  tenantId: string;
  appName: string;
  apiUrl: string;
}

type SettingsTab =
  | 'integrations'
  | 'tenants'
  | 'qa'
  | 'risk'
  | 'users'
  | 'ops-portal'
  | 'model-health';

// ---- Tab definitions ----

const TABS: { id: SettingsTab; label: string }[] = [
  { id: 'integrations', label: 'Integrations' },
  { id: 'tenants', label: 'Tenants' },
  { id: 'qa', label: 'Q/A' },
  { id: 'risk', label: 'Risk' },
  { id: 'users', label: 'Users' },
  { id: 'ops-portal', label: 'Ops Portal' },
  { id: 'model-health', label: 'Model Health' },
];

// ---- OpsPortalEmbed component ----

/**
 * OpsPortalEmbed — fetches config from /api/settings/ops-portal and renders
 * the ops-portal-react iframe embed.
 *
 * ops-portal-react is a React micro-frontend served by the ops-platform service.
 * In Phase 2: import OpsPortalWidget from ops-portal-react package.
 * In Phase 1: embed via iframe using the apiUrl from config.
 */
function OpsPortalEmbed(): React.ReactElement {
  const { user } = useAuth();
  const [config, setConfig] = useState<OpsPortalConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchConfig(): Promise<void> {
      try {
        setLoading(true);
        setError(null);

        // Get JWT from localStorage (dev) or cookie (production)
        const token = localStorage.getItem('crm_dev_token') ?? '';

        const response = await fetch('/api/settings/ops-portal', {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load ops-portal config: ${response.status}`);
        }

        const data = (await response.json()) as OpsPortalConfig;
        setConfig(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
      } finally {
        setLoading(false);
      }
    }

    void fetchConfig();
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-ink-muted">
          <Loader2 className="animate-spin h-5 w-5" />
          <span className="text-sm">Loading ops-portal configuration...</span>
        </div>
      </div>
    );
  }

  if (error !== null) {
    return (
      <div className="rounded-lg border border-fail-soft bg-fail-soft p-6">
        <div className="flex items-start gap-3">
          <CircleAlert className="h-5 w-5 text-fail flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-fail">Failed to load ops-portal</p>
            <p className="text-sm text-fail mt-1">{error}</p>
            <p className="text-xs text-fail mt-2">
              Ensure OPS_PLATFORM_API_KEY and OPS_PLATFORM_URL are configured in the CRM API environment.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (config === null) {
    return <div />;
  }

  // Construct ops-portal embed URL with query params for config
  // Phase 2: use ops-portal-react React component directly
  const opsPortalUrl = new URL(`${config.apiUrl}/embed`);
  opsPortalUrl.searchParams.set('appName', config.appName);
  opsPortalUrl.searchParams.set('tenantId', config.tenantId);

  return (
    <div className="space-y-4">
      {/* Config status bar */}
      <div className="flex items-center gap-2 px-4 py-2 bg-pass-soft border border-pass-soft rounded-lg">
        <div className="w-2 h-2 rounded-full bg-pass" />
        <p className="text-sm text-pass">
          ops-platform connected — <span className="font-medium">{config.appName}</span>
        </p>
        <span className="ml-auto text-xs text-pass">{config.apiUrl}</span>
      </div>

      {/* Embed iframe */}
      <div
        className="rounded-xl border border-line bg-white overflow-hidden"
        style={{ height: '600px' }}
        data-testid="ops-portal-embed"
        data-ops-portal-config={JSON.stringify({
          apiKey: '[REDACTED]',
          tenantId: config.tenantId,
          appName: config.appName,
        })}
      >
        <iframe
          src={opsPortalUrl.toString()}
          title="CU 2.0 ops-portal"
          className="w-full h-full border-0"
          sandbox="allow-scripts allow-same-origin allow-forms"
          loading="lazy"
        />
      </div>

      <p className="text-xs text-ink-muted text-center">
        Powered by CU 2.0 ops-platform — AI governance scan results, compliance flags, audit log
      </p>
    </div>
  );
}

// ---- Settings tab content ----

function IntegrationsTab(): React.ReactElement {
  return (
    <div className="space-y-4">
      <h3 className="text-base font-medium text-ink">Integrations</h3>
      <div className="grid gap-4">
        {[
          { name: 'Symitar / Episys', status: 'configured', detail: 'SymXchange REST v10.x' },
          { name: 'Azure Key Vault', status: 'configured', detail: 'PII tokenization keys' },
          { name: 'Azure Service Bus', status: 'configured', detail: 'cu2.tool.deployments' },
          { name: 'Microsoft Entra ID', status: 'pending', detail: 'SSO federation — setup required' },
          {
            name: 'cu2-prompt-library',
            status: 'configured',
            detail: 'CRM-001 through CRM-LN-008',
          },
        ].map((integration) => (
          <div
            key={integration.name}
            className="flex items-center justify-between p-4 bg-white rounded-lg border border-line"
          >
            <div>
              <p className="text-sm font-medium text-ink">{integration.name}</p>
              <p className="text-xs text-ink-muted mt-0.5">{integration.detail}</p>
            </div>
            <span
              className={cn(
                'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium',
                integration.status === 'configured'
                  ? 'bg-pass-soft text-pass'
                  : 'bg-warn-soft text-warn'
              )}
            >
              {integration.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TenantsTab(): React.ReactElement {
  const [tenants, setTenants] = useState<
    { tenantId: string; cuName: string; status: string; symitar: { healthy: boolean } }[]
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchHealth(): Promise<void> {
      try {
        const token = localStorage.getItem('crm_dev_token') ?? '';
        const response = await fetch('/api/settings/health', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = (await response.json()) as {
            tenants: { tenantId: string; cuName: string; status: string; symitar: { healthy: boolean } }[];
          };
          setTenants(data.tenants);
        }
      } catch {
        // Silently fail — show empty state
      } finally {
        setLoading(false);
      }
    }
    void fetchHealth();
  }, []);

  if (loading) {
    return <p className="text-sm text-ink-muted">Loading tenant health...</p>;
  }

  if (tenants.length === 0) {
    return (
      <div className="text-center py-10">
        <p className="text-sm text-ink-muted">No tenants provisioned yet.</p>
        <p className="text-xs text-ink-muted mt-1">
          Use TenantProvisioningService.provisionTenant() to onboard a pilot CU.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {tenants.map((tenant) => (
        <div
          key={tenant.tenantId}
          className="flex items-center justify-between p-4 bg-white rounded-lg border border-line"
        >
          <div>
            <p className="text-sm font-medium text-ink">{tenant.cuName}</p>
            <p className="text-xs text-ink-muted mt-0.5">ID: {tenant.tenantId}</p>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={cn(
                'text-xs font-medium',
                tenant.symitar.healthy ? 'text-pass' : 'text-ink-muted'
              )}
            >
              Symitar: {tenant.symitar.healthy ? 'healthy' : 'not connected'}
            </span>
            <span
              className={cn(
                'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium',
                tenant.status === 'active' ? 'bg-pass-soft text-pass' : 'bg-sunken text-ink-soft'
              )}
            >
              {tenant.status}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- SettingsPage ----

/**
 * SettingsPage — admin-only settings hub.
 *
 * Tabs:
 * - Integrations: Symitar, Key Vault, Service Bus, Entra ID, prompt-library status
 * - Tenants: Provisioned CU tenants with health status
 * - Q/A: Quality assurance function (per Kirk's standard settings tab)
 * - Risk: Risk function (per Kirk's standard settings tab)
 * - Users: End user management (per Kirk's standard settings tab)
 * - Ops Portal: ops-portal-react iframe embed (ops-platform scan results + audit)
 */
export function SettingsPage(): React.ReactElement {
  const [activeTab, setActiveTab] = useState<SettingsTab>('integrations');

  return (
    <div>
      {/* Page header */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-ink font-heading">Settings</h2>
        <p className="text-sm text-ink-muted mt-1">
          Platform configuration, tenant management, and ops-portal governance — admin only
        </p>
      </div>

      {/* Tab bar */}
      <div className="border-b border-line mb-6">
        <nav className="flex -mb-px space-x-6" aria-label="Settings tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'pb-3 text-sm font-medium border-b-2 transition-colors duration-150 whitespace-nowrap',
                activeTab === tab.id
                  ? 'border-running text-running font-medium'
                  : 'border-transparent text-ink-muted hover:text-ink-soft hover:border-line-strong'
              )}
              aria-current={activeTab === tab.id ? 'page' : undefined}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'integrations' && <IntegrationsTab />}
        {activeTab === 'tenants' && <TenantsTab />}
        {activeTab === 'qa' && (
          <EmptyState
            icon={ClipboardCheck}
            headline="Quality assurance not configured"
            body="Golden test sets and model evaluation scoring will be available in a future release."
          />
        )}
        {activeTab === 'risk' && (
          <EmptyState
            icon={ShieldAlert}
            headline="Risk dashboard not configured"
            body="BSA/AML thresholds, fair lending risk scores, and compliance risk metrics will be available in a future release."
          />
        )}
        {activeTab === 'users' && (
          <EmptyState
            icon={Users}
            headline="No staff accounts configured"
            body="CRM staff accounts with role assignments and Entra ID SSO will be managed here."
            ctaLabel="Configure SSO"
            onCta={() => {}}
          />
        )}
        {activeTab === 'ops-portal' && <OpsPortalEmbed />}
        {activeTab === 'model-health' && <ModelHealthTab />}
      </div>
    </div>
  );
}
